import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { saveAttorney } from "../lib/attorneyAuth";
import { BrandMark } from "../components/Brand";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * Verifies a magic-link token and creates the attorney session.
 * URL: /attorney/magic-verify?token=<token>
 */
export default function AttorneyMagicVerify() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    if (!token) {
      setError("Missing sign-in token.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/attorney/auth/magic-link/verify", { token });
        saveAttorney(data);
        navigate("/attorney/portal", { replace: true });
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F9FC] p-6" data-testid="magic-verify-page">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl px-10 py-12 max-w-md w-full text-center shadow-sm">
        <BrandMark />
        {error ? (
          <div className="mt-7" data-testid="magic-verify-error">
            <AlertTriangle className="w-7 h-7 text-[#B91C1C] mx-auto" />
            <h2 className="font-display text-xl text-[#0A2540] font-semibold mt-4">Sign-in link unavailable</h2>
            <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">{error}</p>
            <Link to="/attorney-login" replace>
              <button className="mt-5 underline text-[#0066FF] text-sm" data-testid="magic-verify-retry">Request a new link</button>
            </Link>
          </div>
        ) : (
          <div className="mt-7">
            <Loader2 className="w-6 h-6 animate-spin text-[#0066FF] mx-auto" />
            <h2 className="font-display text-xl text-[#0A2540] font-semibold mt-5">Signing you in…</h2>
            <p className="text-sm text-[#6B7280] mt-2">Verifying your secure email link.</p>
          </div>
        )}
      </div>
    </div>
  );
}
